import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Location, MaintenanceType, Segment } from '@core/interfaces';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { sentry } from 'sentry';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

const SEARCH_QUERY = gql`
  query searchMaintenanceTypes($where: MaintenanceTypeWhereInput) {
    maintenanceTypes(
      where: $where
      orderBy: name_asc
      limit: 100
      offset: 0
    ){
      items {
        id
        name
      }
    }
  }
`;

@Component({
  selector: 'fc-maintenance-type-input',
  templateUrl: './fc-maintenance-type-input.component.html',
  styleUrls: ['./fc-maintenance-type-input.component.scss']
})
export class FcMaintenanceTypeInputComponent implements OnChanges {

  @Input() public control: FormControl;
  @Input() public displayText: string;
  @Input() public legacyStyle = false;
  @Input() public showSearchIcon = false;
  @Input() public requiredMark = false;
  @Input() public preloadOptions = true;
  @Input() public searchOnFocus = false;
  @Input() public translateErrorPrefix: string;
  @Input() public selectedSegment: Segment;
  @Input() public options = [];
  @Input() public isDuplicatedForm = false;
  @Input() public showAllMaintenanceTypesOption = false;

  constructor(
    private readonly apollo: Apollo,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
    this.search = this.search.bind(this);
    this.displayWith = this.displayWith.bind(this);
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const selectedValuesIsEmpty = changes.control && changes.control.currentValue && changes.control.currentValue.value && (changes.control.currentValue.value.value === '');
    const hasDisplayText = changes.displayText && changes.displayText.currentValue;

    if (selectedValuesIsEmpty && !hasDisplayText) {
      this.control.setValue(null);
    }

    if (changes.selectedSegment && changes.selectedSegment.currentValue) {
      const selectedSegment = changes.selectedSegment.currentValue;
      const hasMaintenanceTypes = !!(selectedSegment.maintenanceTypes && !selectedSegment.maintenanceTypes.length);
      const hasSegmentMaintenanceTypeForms = !!(changes.selectedSegment.currentValue.segmentMaintenanceTypeForms);
      const controlValuesIsNull = changes.control && changes.control.currentValue && changes.control.currentValue.value === null;

      if (hasMaintenanceTypes || (hasSegmentMaintenanceTypeForms && controlValuesIsNull)) {
        this.selectedSegment = null;
        this.options = await this.search('');
        return;
      }

      this.selectedSegment = changes.selectedSegment.currentValue;
      this.options = await this.search('');
    }
  }

  async search(searchTerm: string): Promise<MaintenanceType[]> {
    return this.apollo
      .query({
        query: SEARCH_QUERY,
        variables: {
          where: {
            segmentId: this.selectedSegment && this.selectedSegment.id ? this.selectedSegment.id : null,
            name_contains: searchTerm,
            archived: false
          },
        },
      })
      .toPromise()
      .then((result) => {
        return result.data['maintenanceTypes']['items'];
      })
      .catch((err) => {
        sentry.captureException(err);
        return [];
      });
  }

  displayWith(value: Location): string {
    return value && value.name;
  }
}
